import { Injectable } from '@angular/core';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Equipment, EquipmentLogMapped } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { chain, clone, isEqual, sortBy } from 'lodash';
import * as moment from 'moment-timezone';
import english from '../../../../i18n/en.json';
import spanish from '../../../../i18n/es.json';
import portuguese from '../../../../i18n/pt.json';

@Injectable({
  providedIn: 'root'
})
export class CreateEquipmentLogsService {
  constructor(
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly authenticationService: AuthenticationService,
    private readonly translateService: TranslateService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  public listEquipmentLogsMapped(equipment: Equipment): EquipmentLogMapped[] {
    const equipmentLogsMapped = [];

    const logsSortedByCreatedAt = sortBy(equipment.logs, (equipmentLog) => moment(equipmentLog.createdAt));

    logsSortedByCreatedAt.forEach((currentLog, index) => {
      const previousLog = equipment.logs[index - 1];
      const log = {
        ...currentLog,
        icon: 'ic_plus_20px',
        eventDescription: null,
        eventName: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CREATED.CREATED_TITLE'),
        colorClass: 'light-purple',
        formattedCreatedAt: moment(currentLog.createdAt).local().format('DD/MM/YYYY'),
        createdAt: moment(currentLog.createdAt),
        showMessage: false,
        showCustomFields: false,
        isEquipmentCreatedLog: false
      };

      if (!previousLog) {
        equipmentLogsMapped.push({
          ...log,
          eventDescription: this.translateService.instant(
            'EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CREATED.CREATED_DESCRIPTION',
            {
              number: currentLog.snapshot.number,
              name: currentLog.snapshot.name
            }
          ),
          isEquipmentCreatedLog: true
        });

        return;
      }

      const archiveOrUnarchiveLog = this.generateArchiveOrUnarchiveLog({ log, currentLog, previousLog });
      if (archiveOrUnarchiveLog) {
        equipmentLogsMapped.push(archiveOrUnarchiveLog);
      }

      const locationLog = this.generateLocationLog({ log, previousLog, currentLog });
      if (locationLog) {
        equipmentLogsMapped.push(locationLog);
      }

      const environmentLog = this.generateLocationEnvironmentLog({ log, currentLog, previousLog });
      if (environmentLog) {
        equipmentLogsMapped.push(environmentLog);
      }

      const sectorLog = this.generateLocationSectorLog({ log, currentLog, previousLog });

      if (sectorLog) {
        equipmentLogsMapped.push(sectorLog);
      }

      const nameLog = this.generateNameLog({ log, currentLog, previousLog });
      if (nameLog) {
        equipmentLogsMapped.push(nameLog);
      }

      const numberLog = this.generateNumberLog({ log, currentLog, previousLog });
      if (numberLog) {
        equipmentLogsMapped.push(numberLog);
      }

      const typeLog = this.generateEquipmentTypeLog({ log, currentLog, previousLog });
      if (typeLog) {
        equipmentLogsMapped.push(typeLog);
      }

      const notesLog = this.generateNotesLog({ log, currentLog, previousLog });
      if (notesLog) {
        equipmentLogsMapped.push(notesLog);
      }

      const purchasedAtLog = this.generatePurchasedAtLog({ log, currentLog, previousLog });

      if (purchasedAtLog) {
        equipmentLogsMapped.push(purchasedAtLog);
      }

      const installedAtLog = this.generateInstalledAtLog({ log, currentLog, previousLog });

      if (installedAtLog) {
        equipmentLogsMapped.push(installedAtLog);
      }

      const imageUrlLog = this.generateImageUrlLog({ log, currentLog, previousLog });

      if (imageUrlLog) {
        equipmentLogsMapped.push(imageUrlLog);
      }

      const customFieldsLog = this.generateCustomFieldsLog({ log, currentLog, previousLog });
      if (customFieldsLog) {
        equipmentLogsMapped.push(customFieldsLog);
      }

      const attachmentsLog = this.generateAttachmentsLog({ log, currentLog, previousLog });
      if (attachmentsLog) {
        equipmentLogsMapped.push(attachmentsLog);
      }
    });

    return equipmentLogsMapped;
  }

  private generateCustomFieldsLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    const currentCustomFields = chain(currentLog.snapshot.customFields)
    .map(({ name, value }) => ({ name, value }))
    .sortBy(['name', 'value'])
    .uniqBy('name')
    .value();

    const previousCustomFields = chain(previousLog.snapshot.customFields)
    .map(({ name, value }) => ({ name, value }))
    .sortBy(['name', 'value'])
    .uniqBy('name')
    .value();

    if (isEqual(currentCustomFields, previousCustomFields)) {
      return null;
    }

    const customsFieldsAdded = [];
    const customsFieldsUpdated = [];
    const customsFieldsRemoved = [];

    currentCustomFields.forEach(({ name, value }) => {
      const existingCustomField = previousCustomFields.find((previousCustomField) => {
        return previousCustomField.name === name;
      });

      if (existingCustomField) {
        if (existingCustomField.value !== value) {
          customsFieldsUpdated.push({
            name: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.QUESTION', {
              question: name
            }),
            value: this.normalizeCustomFieldValue(value)
          });
        }
      } else {
        customsFieldsAdded.push({
          name: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.QUESTION', {
            question: name
          }),
          value: this.normalizeCustomFieldValue(value)
        });
      }
    });

    previousCustomFields.forEach(({ name, value }) => {
      const existingCustomField = currentCustomFields.find((currentCustomField) => {
        return currentCustomField.name === name;
      });

      if (!existingCustomField) {
        customsFieldsRemoved.push({
          name: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.QUESTION', {
            question: name
          }),
          value: this.normalizeCustomFieldValue(value)
        });
      }
    });

    const customFieldsLog = clone(log);
    customFieldsLog.eventName = this.translateService.instant(
      'EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.UPDATED'
    );
    customFieldsLog.icon = 'ic_chat_config_20px';
    customFieldsLog.colorClass = 'medium-gray';
    customFieldsLog.customFields = {
      added: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.ANSWER_ADDED'),
        items: customsFieldsAdded
      },
      updated: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.ANSWER_UPDATED'),
        items: customsFieldsUpdated
      },
      removed: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.ANSWER_REMOVED'),
        items: customsFieldsRemoved
      }
    };

    return {
      ...customFieldsLog,
      showCustomFields: true
    };
  }

  private generateAttachmentsLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    const currentAttachments = sortBy(currentLog.snapshot.attachments, (attachment) => moment(attachment.createdAt));
    const previousAttachments = sortBy(previousLog.snapshot.attachments, (attachment) => moment(attachment.createdAt));

    if (isEqual(currentAttachments, previousAttachments)) {
      return null;
    }

    const attachmentsAdded = [];
    const attachmentsRemoved = [];

    currentAttachments.forEach(({ id, path }) => {
      const existingCustomField = previousAttachments.find((previousCustomField) => {
        return previousCustomField.id === id;
      });

      if (!existingCustomField) {
        attachmentsAdded.push({
          id,
          path
        });
      }
    });

    previousAttachments.forEach(({ id, path }) => {
      const existingCustomField = currentAttachments.find((currentCustomField) => {
        return currentCustomField.id === id;
      });

      if (!existingCustomField) {
        attachmentsRemoved.push({
          id,
          path
        });
      }
    });

    const attachmentsLog = clone(log);
    attachmentsLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.ATTACHMENTS.TITLE');
    attachmentsLog.icon = 'ic_attachment_20px';
    attachmentsLog.colorClass = 'light-brown';

    attachmentsLog.attachments = {
      added: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.ATTACHMENTS.ADDED'),
        items: attachmentsAdded
      },
      removed: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.ATTACHMENTS.REMOVED'),
        items: attachmentsRemoved
      }
    };

    return {
      ...attachmentsLog,
      showAttachments: true
    };
  }

  private generateArchiveOrUnarchiveLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    if (currentLog.snapshot.archived === previousLog.snapshot.archived) {
      return null;
    }

    const archiveOrUnarchiveLog = clone(log);

    if (currentLog.snapshot.archived) {
      archiveOrUnarchiveLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.ARCHIVED');
      archiveOrUnarchiveLog.icon = 'ic_archive_18px';
      archiveOrUnarchiveLog.colorClass = 'light-gray';
    } else {
      archiveOrUnarchiveLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.UNARCHIVED');
      archiveOrUnarchiveLog.icon = 'ic_unarchive_18px';
      archiveOrUnarchiveLog.colorClass = 'dark-gray';
    }

    return archiveOrUnarchiveLog;
  }

  private generateLocationLog({ log, previousLog, currentLog }): EquipmentLogMapped {
    if (currentLog.snapshot.location.id === previousLog.snapshot.location.id) {
      return null;
    }

    const locationLog = clone(log);

    locationLog.icon = 'ic_location_18px';
    locationLog.colorClass = 'blue';
    locationLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION.UPDATED');
    locationLog.eventDescription = this.translateService.instant(
      'EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION.DESCRIPTION',
      {
        previousLocation: previousLog.snapshot.location.name,
        currentLocation: locationLog.snapshot.location.name
      }
    );

    return locationLog;
  }

  private generateNumberLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    if (currentLog.snapshot.number === previousLog.snapshot.number) {
      return null;
    }

    const numberLog = clone(log);

    numberLog.icon = 'ic_chat_config_20px';
    numberLog.colorClass = 'medium-gray';
    numberLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NUMBER.UPDATED');
    numberLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NUMBER.DESCRIPTION', {
      previousNumber: previousLog.snapshot.number,
      currentNumber: currentLog.snapshot.number
    });

    return numberLog;
  }

  private generateNameLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    if (currentLog.snapshot.name === previousLog.snapshot.name) {
      return null;
    }

    const nameLog = clone(log);

    nameLog.icon = 'ic_chat_config_20px';
    nameLog.colorClass = 'medium-gray';
    nameLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NAME.UPDATED');
    nameLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NAME.DESCRIPTION', {
      previousName: previousLog.snapshot.name,
      currentName: currentLog.snapshot.name
    });

    return nameLog;
  }

  private generateEquipmentTypeLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    if (currentLog.snapshot.type.id === previousLog.snapshot.type.id) {
      return null;
    }

    const typeLog = clone(log);

    typeLog.icon = 'ic_chat_config_20px';
    typeLog.colorClass = 'medium-gray';
    typeLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.TYPE.UPDATED');
    typeLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.TYPE.DESCRIPTION', {
      previousType: previousLog.snapshot.type.name,
      currentType: currentLog.snapshot.type.name
    });

    return typeLog;
  }

  private generateNotesLog({ log, previousLog, currentLog }): EquipmentLogMapped {
    if (currentLog.snapshot.notes === previousLog.snapshot.notes) {
      return null;
    }

    const notesLog = clone(log);

    if (!previousLog.snapshot.notes && currentLog.snapshot.notes) {
      notesLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.CREATED_TITLE');
    }

    if (
      previousLog.snapshot.notes &&
      currentLog.snapshot.notes &&
      previousLog.snapshot.notes !== currentLog.snapshot.notes
    ) {
      notesLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.UPDATED_TITLE');
    }

    if (previousLog.snapshot.notes && !currentLog.snapshot.notes) {
      notesLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.REMOVED_TITLE');
    }

    notesLog.icon = 'ic_chat_20px';
    notesLog.eventDescription = currentLog.snapshot.notes;
    notesLog.colorClass = 'gray';

    return {
      ...notesLog,
      showMessage: true
    };
  }

  private generatePurchasedAtLog({ log, previousLog, currentLog }): EquipmentLogMapped {
    if (currentLog.snapshot.purchasedAt === previousLog.snapshot.purchasedAt) {
      return null;
    }

    const purchasedAtLog = clone(log);
    purchasedAtLog.colorClass = 'medium-gray';
    purchasedAtLog.icon = 'ic_chat_config_20px';

    if (!previousLog.snapshot.purchasedAt && currentLog.snapshot.purchasedAt) {
      purchasedAtLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.PURCHASED_AT.CREATED.TITLE');
      purchasedAtLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.PURCHASED_AT.CREATED.DESCRIPTION', {
        current: format(new Date(currentLog.snapshot.purchasedAt), 'dd/MM/yyyy')
      });
    }

    if (previousLog.snapshot.purchasedAt && currentLog.snapshot.purchasedAt && previousLog.snapshot.purchasedAt !== currentLog.snapshot.purchasedAt) {
      purchasedAtLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.PURCHASED_AT.UPDATED.TITLE');
      purchasedAtLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.PURCHASED_AT.UPDATED.DESCRIPTION', {
        previous: format(new Date(previousLog.snapshot.purchasedAt), 'dd/MM/yyyy'),
        current: format(new Date(currentLog.snapshot.purchasedAt), 'dd/MM/yyyy')
      });
    }

    if (previousLog.snapshot.purchasedAt && !currentLog.snapshot.purchasedAt) {
      purchasedAtLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.PURCHASED_AT.REMOVED.TITLE');
      purchasedAtLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.PURCHASED_AT.REMOVED.DESCRIPTION', {
        previous: format(new Date(previousLog.snapshot.purchasedAt), 'dd/MM/yyyy')
      });
    }

    return {
      ...purchasedAtLog,
      showMessage: true
    };
  }

  private generateInstalledAtLog({ log, previousLog, currentLog }): EquipmentLogMapped {
    if (currentLog.snapshot.installedAt === previousLog.snapshot.installedAt) {
      return null;
    }

    const installedAtLog = clone(log);
    installedAtLog.colorClass = 'medium-gray';
    installedAtLog.icon = 'ic_chat_config_20px';

    if (!previousLog.snapshot.installedAt && currentLog.snapshot.installedAt) {
      installedAtLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.INSTALLED_AT.CREATED.TITLE');
      installedAtLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.INSTALLED_AT.CREATED.DESCRIPTION', {
        current: format(new Date(currentLog.snapshot.installedAt), 'dd/MM/yyyy')
      });
    }

    if (previousLog.snapshot.installedAt && currentLog.snapshot.installedAt && previousLog.snapshot.installedAt !== currentLog.snapshot.installedAt) {
      installedAtLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.INSTALLED_AT.UPDATED.TITLE');
      installedAtLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.INSTALLED_AT.UPDATED.DESCRIPTION', {
        previous: format(new Date(previousLog.snapshot.installedAt), 'dd/MM/yyyy'),
        current: format(new Date(currentLog.snapshot.installedAt), 'dd/MM/yyyy')
      });
    }

    if (previousLog.snapshot.installedAt && !currentLog.snapshot.installedAt) {
      installedAtLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.INSTALLED_AT.REMOVED.TITLE');
      installedAtLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.INSTALLED_AT.REMOVED.DESCRIPTION', {
        previous: format(new Date(previousLog.snapshot.installedAt), 'dd/MM/yyyy')
      });
    }

    return {
      ...installedAtLog,
      showMessage: true
    };
  }

  private generateImageUrlLog({ log, previousLog, currentLog }): EquipmentLogMapped {
    const isSame = currentLog.snapshot.imageUrl === previousLog.snapshot.imageUrl;
    if (isSame) { return null; }

    const imageUrlLog = clone(log);
    const isCreated = !previousLog.snapshot.imageUrl && currentLog.snapshot.imageUrl;
    const isRemoved = previousLog.snapshot.imageUrl && !currentLog.snapshot.imageUrl;
    const isUpdated = !isCreated && !isRemoved && currentLog.snapshot.imageUrl !== previousLog.snapshot.imageUrl;

    imageUrlLog.icon = 'ic_chat_config_20px';
    imageUrlLog.colorClass = 'medium-gray';

    if (isCreated) {
      imageUrlLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.IMAGE_URL.CREATED');
      imageUrlLog.attachments = {
        added: {
          items: [{ path: currentLog.snapshot.imageUrl }]
        },
      };
    }

    if (isUpdated) {
      imageUrlLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.IMAGE_URL.UPDATED');
      imageUrlLog.attachments = {
        added: {
          items: [{ path: currentLog.snapshot.imageUrl }]
        }
      };
    }

    if (isRemoved) {
      imageUrlLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.IMAGE_URL.REMOVED');
      imageUrlLog.attachments = {
        removed: {
          items: [{ path: previousLog.snapshot.imageUrl }]
        }
      };
    }

    return {
      ...imageUrlLog,
      showAttachments: true
    };
  }

  private generateLocationEnvironmentLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    const currentEnvironmentId = currentLog.snapshot.locationEnvironment && currentLog.snapshot.locationEnvironment.id;
    const previousEnvironmentId = previousLog.snapshot.locationEnvironment && previousLog.snapshot.locationEnvironment.id;

    const isSameEnvironmentId = currentEnvironmentId === previousEnvironmentId;
    if (isSameEnvironmentId) {
      return null;
    }

    const equipmentLog = clone(log);

    const isEnvironmentAdded = !previousEnvironmentId && currentEnvironmentId;
    const isEnvironmentRemoved = previousEnvironmentId && !currentEnvironmentId;
    const isEnvironmentUpdated = !isEnvironmentAdded && !isEnvironmentRemoved && currentEnvironmentId !== previousEnvironmentId;

    equipmentLog.icon = 'icon_space_20px';
    equipmentLog.colorClass = 'dark-blue';

    const params = {
      previous: previousEnvironmentId && previousLog.snapshot.locationEnvironment.name,
      current: currentEnvironmentId && currentLog.snapshot.locationEnvironment.name
    };

    if (isEnvironmentAdded) {
      equipmentLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_ENVIRONMENT.CREATED');
      equipmentLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_ENVIRONMENT.CREATED_DESCRIPTION', params);
    }

    if (isEnvironmentRemoved) {
      equipmentLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_ENVIRONMENT.REMOVED');
      equipmentLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_ENVIRONMENT.REMOVED_DESCRIPTION', params);
    }


    if (isEnvironmentUpdated) {
      equipmentLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_ENVIRONMENT.UPDATED');
      equipmentLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_ENVIRONMENT.UPDATED_DESCRIPTION', params);
    }

    return equipmentLog;
  }

  private generateLocationSectorLog({ log, currentLog, previousLog }): EquipmentLogMapped {
    const currentSectorId = currentLog.snapshot.locationSector && currentLog.snapshot.locationSector.id;
    const previousSectorId = previousLog.snapshot.locationSector && previousLog.snapshot.locationSector.id;

    const isSameSectorId = currentSectorId === previousSectorId;
    if (isSameSectorId) {
      return null;
    }

    const equipmentLog = clone(log);

    const isSectorAdded = !previousSectorId && currentSectorId;
    const isSectorRemoved = previousSectorId && !currentSectorId;
    const isSectorUpdated = !isSectorAdded && !isSectorRemoved && currentSectorId !== previousSectorId;

    equipmentLog.icon = 'icon_space_20px';
    equipmentLog.colorClass = 'dark-blue';

    const params = {
      previous: previousSectorId && previousLog.snapshot.locationSector.name,
      current: currentSectorId && currentLog.snapshot.locationSector.name
    };

    if (isSectorAdded) {
      equipmentLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_SECTOR.CREATED');
      equipmentLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_SECTOR.CREATED_DESCRIPTION', params);
    }

    if (isSectorRemoved) {
      equipmentLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_SECTOR.REMOVED');
      equipmentLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_SECTOR.REMOVED_DESCRIPTION', params);
    }

    if (isSectorUpdated) {
      equipmentLog.eventName = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_SECTOR.UPDATED');
      equipmentLog.eventDescription = this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION_SECTOR.UPDATED_DESCRIPTION', params);
    }

    return equipmentLog;
  }

  private normalizeCustomFieldValue(value): string {
    const datePattern = /^\d{4}-\d{2}-\d{2}/;
    return datePattern.test(value) ? moment(value).local().format('DD/MM/YYYY') : value;
  }
}
